// src/hooks/useCreateMapExploration.ts
//
// Imperative hook that calls the createMapExploration Cloud Function.
// The function returns 202 immediately and writes the result to the
// conversation doc — callers should subscribe to that doc (via the
// conversationStore) to see the result.
//
// Mirrors the shape of useWalkingTour. Same auth-token + body
// pattern; same fire-and-watch flow:
//
//   1. Caller invokes generate({ conversationId, query }).
//   2. Function POSTs to createMapExploration with the user's auth
//      token + location + language hint.
//   3. Function flips conversation.map_exploration.status to
//      'generating' (already done by the seed during creation, but
//      the server re-asserts it during retries) and returns 202.
//   4. Function does AI + Places work in the background and writes
//      conversation.map_exploration.status = 'ready' with the
//      ranked place list.
//   5. Caller's conversation snapshot listener picks up the change
//      and re-renders.
//
// The hook itself owns no state about the in-flight request beyond
// `requesting` / `error` flags — the source of truth is the
// conversation doc. This matches the pattern in useWalkingTour and
// keeps the UI from going out of sync with persisted state.

import { useCallback, useState } from 'react';
import { auth } from '../services/firebase';
import { useLocation } from './useLocation';
import { useAuthStore } from '../stores/authStore';
import { getLanguageHint } from '../utils/getLanguageHint';

const FUNCTION_URL =
  'https://us-central1-travelx-46b7a.cloudfunctions.net/createMapExploration';

export function useCreateMapExploration() {
  const { location } = useLocation();
  const profile = useAuthStore((s) => s.profile);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(
    async (params: {
      conversationId: string;
      query: string;
    }): Promise<boolean> => {
      const user = auth.currentUser;
      if (!user) {
        setError('Sign in to explore the map');
        return false;
      }
      if (!location?.latitude || !location?.longitude) {
        setError('Location required to explore the map');
        return false;
      }
      const query = params.query?.trim();
      if (!query) {
        setError('Query required');
        return false;
      }
      setRequesting(true);
      setError(null);
      try {
        const token = await user.getIdToken();
        const resp = await fetch(FUNCTION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            auth_token: token,
            conversation_id: params.conversationId,
            query,
            location: {
              latitude: location.latitude,
              longitude: location.longitude,
            },
            // Forwarded so blurbs land in the user's language.
            // Place names stay in their canonical language —
            // that's controlled inside the cloud function.
            language: getLanguageHint(profile),
          }),
        });
        if (!resp.ok && resp.status !== 202) {
          const text = await resp.text().catch(() => '');
          throw new Error(`HTTP ${resp.status}: ${text.slice(0, 200)}`);
        }
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        console.warn('createMapExploration failed:', msg);
        setError(msg);
        return false;
      } finally {
        setRequesting(false);
      }
    },
    [location, profile]
  );

  return { generate, requesting, error };
}
